// Turning a tracking number into somewhere you can actually click.
//
// Raised from inside the app: "we can see the tracking number for the
// offer sent, but cannot quickly access the live tracking page."
//
// The obvious move — linking the tracking_url column that already
// exists on certified_mail_jobs — is wrong. That column holds
// PostGrid's own letter PDF (api/_lib/postgrid.js writes `body.url`
// into it), served from S3 behind a signed `Expires=` parameter. The
// links on the live rows expired days after they were written, so it
// is neither a tracking page nor a lasting one. The carrier's page has
// to be built from the number.
//
// When the carrier cannot be established, this returns null and the
// caller renders no link. A tracking link that opens the wrong
// carrier's "not found" page is worse than the number on its own,
// because the number can still be pasted somewhere that works.

const CARRIERS = Object.freeze({
  usps: {
    label: 'USPS',
    url: (n) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${encodeURIComponent(n)}`,
  },
  ups: {
    label: 'UPS',
    url: (n) => `https://www.ups.com/track?tracknum=${encodeURIComponent(n)}`,
  },
  fedex: {
    label: 'FedEx',
    url: (n) => `https://www.fedex.com/fedextrack/?trknbr=${encodeURIComponent(n)}`,
  },
});

/** Carriers print these with spaces and dashes; nobody stores them consistently. */
export function normalizeTrackingNumber(value) {
  return String(value || '').replace(/[\s-]/g, '').toUpperCase();
}

/**
 * Which carrier carries this number.
 *
 * The provider is asked first, because it is a fact about who we handed
 * the letter to rather than an inference from the digits. Everything we
 * post through PostGrid goes as USPS certified mail.
 */
export function carrierForTracking(trackingNumber, provider = '') {
  const n = normalizeTrackingNumber(trackingNumber);
  if (!n) return '';
  if (String(provider || '').trim().toLowerCase() === 'postgrid') return 'usps';

  // UPS is the one unambiguous prefix in the set.
  if (/^1Z[0-9A-Z]{16}$/.test(n)) return 'ups';
  // UPU S10, used for USPS international.
  if (/^[A-Z]{2}\d{9}[A-Z]{2}$/.test(n)) return 'usps';

  if (!/^\d+$/.test(n)) return '';
  // USPS Intelligent Mail package barcodes.
  if (n.length >= 20 && n.length <= 22 && /^9[2-5]/.test(n)) return 'usps';
  // FedEx Ground's 96-prefixed barcode is the same length, different head.
  if (n.length === 22 && n.startsWith('96')) return 'fedex';
  if (n.length === 12 || n.length === 15) return 'fedex';
  // A bare number of some other shape belongs to nobody we can name.
  return '';
}

/**
 * The link for one leg of a mail job, or null when we cannot say.
 *
 * `returnLeg` asks about the envelope coming back rather than the one
 * going out — they are different numbers and a deed packet has both.
 */
export function trackingLink(job, { returnLeg = false } = {}) {
  if (!job) return null;
  const number = returnLeg
    ? (job.returnTrackingNumber || job.return_tracking_number || '')
    : (job.trackingNumber || job.tracking_number || '');
  const clean = normalizeTrackingNumber(number);
  if (!clean) return null;
  const carrier = carrierForTracking(clean, job.provider || '');
  const entry = CARRIERS[carrier];
  if (!entry) return null;
  return {
    carrier,
    label: entry.label,
    number: String(number).trim(),
    url: entry.url(clean),
  };
}
